import * as React from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { GetSnapshotEventResponseItem } from "@/modules/snapshots/businessReports.types";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface Props {
  events: GetSnapshotEventResponseItem[];
  disabled?: boolean;
  onViewEvent: (eventId: string) => void;
  variant?: "default" | "dark";
}

export const EventsDropdown: React.FC<Props> = ({ events, disabled, onViewEvent, variant = "default" }) => {
  const { formatDateShort } = useDateFormatter();

  if (events.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "dark" ? (
          <button
            className="flex items-center rounded-md bg-[#585451] p-1 text-xs text-white disabled:opacity-50"
            disabled={disabled}
          >
            <Calendar size={14} className="mr-1" />
            Events ({events.length})
            <ChevronDown size={12} className="ml-1" />
          </button>
        ) : (
          <Button variant="outline" size="sm" disabled={disabled} className="h-8 gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            Events ({events.length})
            <ChevronDown className="h-3 w-3" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {events.map(event => (
          <DropdownMenuItem key={event.id} onClick={() => onViewEvent(event.id)} className="cursor-pointer">
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">{event.title || event.event_type}</span>
              <span className="text-xs text-muted-foreground">{formatDateShort(event.event_date)}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
